import { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[#1a4d2e]/95 backdrop-blur-sm shadow-lg' : 'bg-[#1a4d2e]/85'
    }`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img 
              src="https://static.readdy.ai/image/59a97b6adbe04686d45399216c04b197/872de3acd12df5063e37cc28860c7214.webp" 
              alt="Environmental Data Consultancy Logo" 
              className="h-12 w-12 object-contain"
            />
            <span className="text-white font-semibold text-lg whitespace-nowrap">EcoDataGeo Solutions</span>
          </div>

          <div className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollToSection('home')} className="text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.home')}</button>
            <button onClick={() => scrollToSection('services')} className="text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.services')}</button>
            <button onClick={() => scrollToSection('portfolio')} className="text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.portfolio')}</button>
            <button onClick={() => scrollToSection('about')} className="text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.about')}</button>
            <button onClick={() => scrollToSection('publications')} className="text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.publications')}</button>
            <button onClick={() => scrollToSection('contact')} className="text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.contact')}</button>
            
            <div className="relative">
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg text-[#f5f1e8] hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-global-line text-lg"></i>
                <span className="text-sm font-medium">{language === 'pt' ? 'PT' : 'EN'}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-[#1a4d2e] rounded-lg shadow-xl border border-white/10 overflow-hidden min-w-[140px]">
                  <button 
                    onClick={() => { setLanguage('pt'); setIsLangMenuOpen(false); }}
                    className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/10 transition-colors cursor-pointer ${language === 'pt' ? 'text-[#ff6b4a]' : 'text-[#f5f1e8]'}`}
                  >
                    <span className="text-lg">🇧🇷</span>
                    Português
                  </button>
                  <button 
                    onClick={() => { setLanguage('en'); setIsLangMenuOpen(false); }}
                    className={`w-full px-4 py-3 text-left text-sm flex items-center gap-3 hover:bg-white/10 transition-colors cursor-pointer ${language === 'en' ? 'text-[#ff6b4a]' : 'text-[#f5f1e8]'}`}
                  >
                    <span className="text-lg">🇺🇸</span>
                    English
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <div className="relative">
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded-lg text-[#f5f1e8] hover:bg-white/20 transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium">{language === 'pt' ? 'PT' : 'EN'}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLangMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-[#1a4d2e] rounded-lg shadow-xl border border-white/10 overflow-hidden min-w-[130px] z-50">
                  <button 
                    onClick={() => { setLanguage('pt'); setIsLangMenuOpen(false); }}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-white/10 transition-colors cursor-pointer ${language === 'pt' ? 'text-[#ff6b4a]' : 'text-[#f5f1e8]'}`}
                  >
                    <span>🇧🇷</span>
                    Português
                  </button>
                  <button 
                    onClick={() => { setLanguage('en'); setIsLangMenuOpen(false); }}
                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-white/10 transition-colors cursor-pointer ${language === 'en' ? 'text-[#ff6b4a]' : 'text-[#f5f1e8]'}`}
                  >
                    <span>🇺🇸</span>
                    English
                  </button>
                </div>
              )}
            </div>
            
            <button 
              className="text-white w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-3">
            <button onClick={() => scrollToSection('home')} className="block text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.home')}</button>
            <button onClick={() => scrollToSection('services')} className="block text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.services')}</button>
            <button onClick={() => scrollToSection('portfolio')} className="block text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.portfolio')}</button>
            <button onClick={() => scrollToSection('about')} className="block text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.about')}</button>
            <button onClick={() => scrollToSection('publications')} className="block text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.publications')}</button>
            <button onClick={() => scrollToSection('contact')} className="block text-[#f5f1e8] hover:text-[#ff6b4a] transition-colors duration-300 whitespace-nowrap cursor-pointer">{t('nav.contact')}</button>
          </div>
        )}
      </div>
    </nav>
  );
}
